import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useState } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { TextRenderer } from "../RichText/TextRenderer";
import { defaultStyles } from "./defaultStyles";

interface ButtonStylesSet {
  default: Record<string, StyleValue>;
  hover: Record<string, StyleValue>;
  margin: StyleValue;
  padding: StyleValue;
  sizing: Record<string, StyleValue>;
}

interface ButtonProps extends ComponentPropsWithRef<"a"> {
  blockPath: string;
  "data-preview-mode": boolean;
  href: string;
  styles: ButtonStylesSet;
  text: string;
}

export const SecondaryButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      blockPath,
      text,
      href,
      styles,
      className,
      onClick,
      target = "_self",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const currentStyles = isHovered ? styles.hover : styles.default;
    const inlineStyles = {
      ...parseStyles({
        styles: currentStyles,
        defaultStyles: isHovered ? defaultStyles.hover : defaultStyles.default,
      }),
      ...parseStyles({
        styles: styles.sizing,
        defaultStyles: defaultStyles.sizing,
      }),
      ...parseStyles({
        styles: { margin: styles.margin },
        defaultStyles: { margin: defaultStyles.margin },
      }),
      ...parseStyles({
        styles: { padding: styles.padding },
        defaultStyles: { padding: defaultStyles.padding },
      }),
    };

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
        onClick?.(event);
      },
      [isPreviewMode, onClick],
    );

    return (
      <a
        ref={ref}
        className={classNames(
          "wb-inline-flex",
          "wb-min-h-fit wb-min-w-fit wb-items-center wb-justify-center wb-transition-colors",
          "wb-pt-button-secondary-top wb-pb-button-secondary-bottom wb-pl-button-secondary-left wb-pr-button-secondary-right",
          "wb-text-button-secondary wb-font-button-secondary wb-leading-button-secondary wb-tracking-button-secondary wb-text-center",
          "wb-bg-button-secondary wb-text-button-secondary-text wb-border-button-secondary",
          "wb-rounded-tl-button-secondary-default-left-top wb-rounded-tr-button-secondary-default-right-top wb-rounded-bl-button-secondary-default-left-bottom wb-rounded-br-button-secondary-default-right-bottom",
          "wb-shadow-button-secondary",
          "hover:wb-bg-button-secondary-hover hover:wb-text-button-secondary-text-hover",
          "hover:wb-border-button-secondary-hover",
          "hover:wb-rounded-tl-button-secondary-hover-left-top hover:wb-rounded-tr-button-secondary-hover-right-top hover:wb-rounded-bl-button-secondary-hover-left-bottom hover:wb-rounded-br-button-secondary-hover-right-bottom",
          "hover:wb-font-button-secondary-hover hover:wb-leading-button-secondary-hover hover:wb-tracking-button-secondary-hover",
          "hover:wb-shadow-button-secondary-hover",
          className,
        )}
        style={inlineStyles}
        href={href}
        target={target}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        rel="noreferrer noopener"
        {...rest}
      >
        <TextRenderer blockPath={blockPath} text={text} />
      </a>
    );
  },
);

SecondaryButton.displayName = "SecondaryButton";
